/**
 * Screen Router
 * Handles routing between verification screens using React Router
 * Now with browser history integration and state dependency guards
 */

import { useEffect, Suspense } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useScreenFlow } from '../hooks/useScreenFlow';
import type { ScreenType } from '../types/screens';
import { logger } from '../utils/logger';
import { sessionStateManager } from '../utils/SessionStateManager';
import { runtimeConfig } from '../services/RuntimeConfigService';
import { PATH_TO_SCREEN, SCREEN_PATHS } from '../utils/screenPaths';
import { RouteGuard } from './shared/RouteGuard';
import { CameraErrorBoundary } from './shared';
import { MobileCompletionErrorBoundary } from './shared/MobileCompletionErrorBoundary';
import { verificationLifecycleService } from '../services/VerificationLifecycleService';
import {
  RecoverableLazyScreen,
} from './shared/RecoverableLazyScreen';
import {
  lazyScreenLoaders,
  type LazyScreenType,
} from './lazyScreenLoaders';

const routerLogger = logger.clone({ labels: { component: 'ScreenRouter' }});

// Keep critical screens eager loaded
import { MainScreen } from './screens/MainScreen';
import { LoadingScreen } from './screens/LoadingScreen';
import { NeutralSpinner } from './shared/NeutralSpinner';

// The stable import functions are shared, but React.lazy is created inside
// RecoverableLazyScreen so an explicit retry receives a fresh lazy identity.
const lazyScreen = (screen: LazyScreenType) => (
  <RecoverableLazyScreen screen={screen} loader={lazyScreenLoaders[screen]} />
);

export function ScreenRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, manager } = useScreenFlow();
  const currentScreen = state.currentScreen;
  const expectedPath = SCREEN_PATHS[currentScreen];
  const needsPathSync =
    currentScreen !== 'main' && location.pathname !== expectedPath;
  const sessionState = sessionStateManager.getState();
  const hasStoredSession =
    !!sessionState?.sessionId &&
    !!(sessionState?.config?.sessionToken ||
      sessionState?.config?.apiKey ||
      sessionState?.config?.returnUrl);
  const hasRuntimeSession = (() => {
    if (!runtimeConfig.isLoaded()) {
      return false;
    }
    const config = runtimeConfig.getConfig();
    return !!(config.sessionId || config.sessionToken || config.handoffToken);
  })();
  const hasSessionContext = hasStoredSession || hasRuntimeSession;
  const currentPathScreen = PATH_TO_SCREEN[location.pathname];
  const isPublicScreen =
    currentPathScreen === 'main' || currentPathScreen === 'loading';
  const shouldBlockDirectAccess =
    !!currentPathScreen && !isPublicScreen && !hasSessionContext;

  // IMPORTANT: On first render, if currentScreen doesn't match the URL path,
  // navigate to the correct path for currentScreen (don't sync screen to URL)
  // This ensures skip_intro and other direct navigation features work correctly
  useEffect(() => {
    if (needsPathSync) {
      navigate(expectedPath, { replace: true });
    }
  }, [expectedPath, navigate, needsPathSync]);

  // Browser history is a navigation request, never lifecycle authority. Every
  // back/forward transition is projected from Portal before local flow state
  // changes; browser-local transition latches do not preempt reconciliation.
  useEffect(() => {
    const handlePopState = async () => {
      const currentPath = window.location.pathname;
      const targetScreen = PATH_TO_SCREEN[currentPath];

      if (!targetScreen || targetScreen === state.currentScreen) {
        return;
      }

      try {
        const lifecycle = await verificationLifecycleService.reconcile();
        const authoritativeScreen = verificationLifecycleService.screenFor(lifecycle);
        if (authoritativeScreen !== targetScreen) {
          routerLogger.info('Browser history reconciled to the current verification step');
          manager.navigateToHistoryScreen(authoritativeScreen);
          navigate(SCREEN_PATHS[authoritativeScreen], { replace: true });
          return;
        }
      } catch (error) {
        routerLogger.warn('Browser history state could not be confirmed', {
          labels: { error: error instanceof Error ? error.message : String(error) },
        });
        navigate(SCREEN_PATHS[state.currentScreen], { replace: true });
        return;
      }

      routerLogger.debug(
        `Browser navigation detected: ${state.currentScreen} -> ${targetScreen}`
      );

      // Use the new navigateToHistoryScreen method for browser navigation
      manager.navigateToHistoryScreen(targetScreen);
    };

    const onPopState = () => void handlePopState();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [state.currentScreen, manager, navigate]);

  // Set up navigation callback for ScreenFlowManager
  useEffect(() => {
    const navigationCallback = (from: ScreenType, to: ScreenType) => {
      const path = SCREEN_PATHS[to];
      if (path && location.pathname !== path) {
        routerLogger.debug(
          `Pushing history state for screen: ${from} -> ${to}, path: ${path}`
        );
        navigate(path);
      }
    };

    manager.setNavigationCallback(navigationCallback);
  }, [manager, navigate, location.pathname]);

  // Initial/direct access is handled here. A mapped pathname mismatch is not
  // navigation authority: ordinary ScreenFlow transitions project their URL
  // through the callback above, while browser history is owned by popstate.
  useEffect(() => {
    if (needsPathSync) {
      return;
    }
    if (shouldBlockDirectAccess) {
      routerLogger.warn('Direct navigation blocked - missing session context', {
        labels: {
          path: location.pathname,
          targetScreen: currentPathScreen || 'unknown',
        },
      });
      try {
        sessionStorage.setItem('sp_direct_access_blocked', 'true');
      } catch {
        // Ignore storage failures to keep navigation resilient
      }
      navigate(SCREEN_PATHS.main, { replace: true });
      return;
    }
    if (location.pathname !== expectedPath && !currentPathScreen) {
      // Unknown path, navigate to the authoritative ScreenFlow route.
      routerLogger.debug(`Navigating to expected path: ${expectedPath}`);
      navigate(expectedPath, { replace: true });
    }
  }, [
    currentPathScreen,
    expectedPath,
    location.pathname,
    navigate,
    needsPathSync,
    shouldBlockDirectAccess,
  ]);

  routerLogger.debug(
    `Rendering routes with currentScreen=${currentScreen}, location=${location.pathname}`
  );

  if (needsPathSync) {
    return <NeutralSpinner />;
  }

  if (shouldBlockDirectAccess) {
    return <NeutralSpinner />;
  }

  return (
    <Suspense fallback={<NeutralSpinner />}>
      <Routes>
        <Route path="/" element={<MainScreen />} />
        <Route path="/emblem-step-up" element={lazyScreen('emblem-step-up')} />
        <Route path="/loading" element={<LoadingScreen />} />
        <Route path="/camera-access" element={lazyScreen('camera-access')} />
        <Route
          path="/camera-setup"
          element={
            <CameraErrorBoundary>
              {lazyScreen('camera-setup')}
            </CameraErrorBoundary>
          }
        />
        <Route path="/desktop-waiting" element={lazyScreen('desktop-waiting')} />
        <Route path="/auto-return" element={lazyScreen('auto-return')} />
        <Route path="/success" element={lazyScreen('success')} />
        <Route path="/failure" element={lazyScreen('failure')} />

        {/* Protected routes with state dependencies */}
        <Route
          path="/id-request"
          element={
            <RouteGuard requiredData={['selfie']}>
              {lazyScreen('id-request')}
            </RouteGuard>
          }
        />
        <Route
          path="/id-capture"
          element={
            <RouteGuard requiredData={['selfie']}>
              <CameraErrorBoundary>
                {lazyScreen('id-capture')}
              </CameraErrorBoundary>
            </RouteGuard>
          }
        />
        <Route
          path="/id-review"
          element={
            <RouteGuard requiredData={['selfie', 'documentImage']}>
              {lazyScreen('id-review')}
            </RouteGuard>
          }
        />
        <Route
          path="/id-processing"
          element={
            <RouteGuard requiredData={['selfie', 'documentImage']}>
              {lazyScreen('id-processing')}
            </RouteGuard>
          }
        />
        {/* Face-match recovery: Portal-owned state, no local prerequisites. */}
        <Route path="/selfie-retry" element={lazyScreen('selfie-retry')} />

        {/* Mobile QR completion screen with error boundary */}
        <Route 
          path="/return-to-desktop" 
          element={
            <MobileCompletionErrorBoundary>
              {lazyScreen('return-to-desktop')}
            </MobileCompletionErrorBoundary>
          } 
        />

        {/* Catch all - redirect to main */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
